import axios, {AxiosInstance, AxiosResponse, CreateAxiosDefaults, RawAxiosRequestHeaders} from 'axios'

import {setupInterceptors} from './interceptors'
import {
    CandidateCountParams,
    CandidatePoolParams,
    CompanySizeType,
    DeepPartialCandidateProfile,
    DesJobType,
    IAlertConfig,
    ICandidate,
    ICourse,
    IJobDemand,
    IJobDetails,
    ILocation,
    IMessage,
    IMessageBuble,
    IPosition,
    IPositions,
    ISearchResult,
    JobExpType,
    JobTypeOption,
    Locations,
    MatchingPositionsType,
    OutreachRequest,
    OutreachResponse,
    OutreachTemplate,
    OutreachTemplateRequest,
    OutreachTemplateResponse,
    Radius,
    SectorType,
    SendJobCoachMessageRequest,
    SendJobCoachMessageResponse,
    StartJobCoachRequest,
    StartJobCoachResponse
} from "./types.ts";
import {User} from "../context/AuthContext.tsx";
import {getToken} from "../util/tokenCookies.ts";
import {notification} from "antd";

const getHeaders = (): RawAxiosRequestHeaders => {
    const headers: RawAxiosRequestHeaders = {};
    if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
        headers["X-Data-Source"] = "staging";
    }

    return headers;
};

const authConfig: CreateAxiosDefaults = {
    baseURL: import.meta.env.VITE_AUTH_URL as string,
    headers: getHeaders()
};

const baseConfig: CreateAxiosDefaults = {
    baseURL: import.meta.env.VITE_BASE_URL as string,
    headers: getHeaders()
};

const billingConfig: CreateAxiosDefaults = {
    baseURL: import.meta.env.VITE_BILLING_URL as string,
    headers: getHeaders()
};

const privacyConfig: CreateAxiosDefaults = {
    baseURL: import.meta.env.VITE_PRIVACY_URL as string,
    headers: getHeaders()
};
const coursesConfig: CreateAxiosDefaults = {
    baseURL: import.meta.env.VITE_COURSES_BASE_URL as string,
    headers: getHeaders()
};

const jobCoachConfig: CreateAxiosDefaults = {
    baseURL: import.meta.env.VITE_AGENT_API_BASE_URL as string,
    headers: getHeaders()
};

const xanoAiConfig: CreateAxiosDefaults = {
    baseURL: import.meta.env.VITE_AI_URL as string,
    headers: getHeaders()
};

export const authApi: AxiosInstance = setupInterceptors(axios.create(authConfig))
export const baseApi: AxiosInstance = setupInterceptors(axios.create(baseConfig))
export const billingApi: AxiosInstance = setupInterceptors(axios.create(billingConfig))
export const privacyApi: AxiosInstance = setupInterceptors(axios.create(privacyConfig))
export const coursesApi: AxiosInstance = setupInterceptors(axios.create(coursesConfig))
export const jobCoachApi: AxiosInstance = setupInterceptors(axios.create(jobCoachConfig))
export const xanoAiApi: AxiosInstance = setupInterceptors(axios.create(xanoAiConfig))

export const getUserApi = async () => {
    const {data} = await authApi.get('/auth/me')
    return data
}

export const loginUserApi = async (email: string, password: string) => {
    try {
        const {data} = await authApi.post('/auth/login', {email, password});
        return data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const errorMessage = error.response?.data?.message || 'Bei der Anmeldung ist ein Fehler aufgetreten';

            notification.error({
                message: 'Anmeldung fehlgeschlagen',
                description: errorMessage,
            });
        } else {
            notification.error({
                message: 'Anmeldung fehlgeschlagen',
                description: 'Bei der Anmeldung ist ein Fehler aufgetreten',
            });
        }
        throw error;
    }
};

export const changeUserEmailApi = async (newEmail: string, currentUserData: User) => {
    const updatedUserData = {
        ...currentUserData,
        email: newEmail,
    } as User;

    const {data}: { data: User } = await authApi.put(`/users/${updatedUserData.id}`, updatedUserData);
    return data;
};

export const signupUserApi = async (email: string, password: string, telephone: string) => {
    const {data} = await authApi.post('/auth/signup', {
        email,
        password,
        role: "client",
        sessionId: telephone,
        pd_person_id: "",
        pd_deal_id: ""
    })
    return data
}

export const getJobPool = async ({limit, offset, positionId, locationId}: {
    limit: number,
    offset: number,
    positionId?: number,
    locationId?: number
}) => {
    const params: CandidatePoolParams = {
        "mode": 'lean',
        limit,
        offset,
    };

    if (positionId) {
        params.search_what_id = positionId;
    }

    if (locationId) {
        params.search_location_id = locationId;
    }

    const {data}: { data: IJobDetails[] } = await baseApi.get('/job_demand_v2', {params});
    return data;
};

export const getJobPoolCount = async (position?: IPosition, location?: ILocation) => {
    const params: CandidateCountParams = {
        mode: "count",
    };
    if (position) {
        params.search_what_id = position.id;
    }

    if (location) {
        params.search_location_id = location.id;
    }

    const {data}: { data: { id: number } } = await baseApi.get('/job_demand', {
        params
    });
    return data;
};

export const getSearchWhat = async (): Promise<IPositions> => {
    const {data}: { data: IPositions } = await baseApi.get('/search_what', {
        params: {mode: "modal"}
    });
    return data;
};

export const getSearchLocation = async (location: string): Promise<Locations> => {
    const {data}: { data: Locations } = await baseApi.get('/search_location', {
        params: {mode: "autocomplete", search: location}
    });
    return data;
};

export const addJobDemand = async (jobDemand: IJobDemand | Partial<IJobDemand>) => {
    const {data} = await baseApi.post('/job_demand', jobDemand);
    return data;
};

export const getAllOutreachTemplates = async (): Promise<OutreachTemplate[]> => {
    try {
        const {data}: { data: OutreachTemplate[] } = await baseApi.get('/outreach_template');
        return data;
    } catch (error) {
        console.error('Error fetching outreach templates:', error);
        throw error;
    }
};

export const getJobDemand = async (params: { mode: string }): Promise<{ id: number, title: string }[]> => {
    const {data}: { data: { id: number, title: string }[] } = await baseApi.get('/job_demand', {params});
    return data;
};

export const getUserDetails = async (userId: string): Promise<Partial<User>> => {
    const {data}: { data: Partial<User> } = await authApi.get(`/users/${userId}`);
    return data;
};

export const getCandidateDetails = async (): Promise<ICandidate> => {
    const {data} = await authApi.get(`/candidate`);
    return data[0];
};

export const getCandidateAgentDetails = async (id: string): Promise<ICandidate> => {
    const {data}:{data: ICandidate} = await authApi.get(`/candidate/${id}/agent`);
    return data;
};

export const addOutreachTemplate = async (template: OutreachTemplateRequest): Promise<OutreachTemplateResponse> => {
    try {
        const {data}: AxiosResponse<OutreachTemplateResponse> = await baseApi.post('/outreach_template', template);
        return data;
    } catch (error) {
        console.error('Error adding outreach template:', error);
        throw error;
    }
};

export const addOutreach = async (outreach: OutreachRequest): Promise<OutreachResponse> => {
    try {
        const {data}: AxiosResponse<OutreachResponse> = await baseApi.post('/application', outreach);
        return data;
    } catch (error) {
        console.error('Error adding outreach:', error);
        throw error;
    }
};

export const updateOutreachTemplate = async (template: OutreachTemplateRequest): Promise<OutreachTemplateResponse> => {
    try {
        const {data}: AxiosResponse<OutreachTemplateResponse> = await baseApi.post(`/outreach_template/${template.id}`, template);
        return data;
    } catch (error) {
        console.error('Error updating outreach template:', error);
        throw error;
    }
};

export const deleteOutreachTemplate = async (id: number): Promise<void> => {
    try {
        await baseApi.delete(`/outreach_template/${id}`);
    } catch (error) {
        console.error('Error deleting outreach template:', error);
        throw error;
    }
};


export const getJobTypes = async (): Promise<JobTypeOption[]> => {
    const {data}: { data: JobTypeOption[] } = await baseApi.get<JobTypeOption[]>('/job_type');
    return data;
};

export const getHomeofficeOptions = async (): Promise<JobTypeOption[]> => {
    const {data}: { data: JobTypeOption[] } = await baseApi.get<JobTypeOption[]>('/homeoffice');
    return data;
};

export const getWorkloadOptions = async (): Promise<JobTypeOption[]> => {
    const {data}: { data: JobTypeOption[] } = await baseApi.get<JobTypeOption[]>('/work_load');
    return data;
};

export const getTargetGroupOptions = async (): Promise<JobTypeOption[]> => {
    const {data}: { data: JobTypeOption[] } = await baseApi.get<JobTypeOption[]>('/target_group');
    return data;
};

export const getJobs = async ({limit, offset}: { limit: number, offset: number }): Promise<IJobDetails[]> => {
    const {data}: AxiosResponse<IJobDetails[]> = await baseApi.get<IJobDetails[]>('/job_demand', {
        params: {
            limit,
            offset
        }
    });
    return data;
};

export const getJobById = async (id: string): Promise<IJobDetails> => {
    const {data}: AxiosResponse<IJobDetails> = await baseApi.get<IJobDetails>(`/job_demand/${id}`);
    return data;
};

export const getJobByKey = async (key: string): Promise<IJobDetails[]> => {
    const params = {mode: "key", key: key}
    const {data}: AxiosResponse<IJobDetails[]> = await baseApi.get<IJobDetails[]>(`/job_demand`, {params});
    return data;
};

export const getMessagesByKey = async (key: string): Promise<IJobDetails[]> => {
    const params = {mode: "key", key: key}
    const {data}: AxiosResponse<IJobDetails[]> = await baseApi.get<IJobDetails[]>(`/communication`, {params});
    return data;
};

export const deleteJobDemand = async (id: string): Promise<void> => {
    await baseApi.delete(`/job_search/${id}`);
};

export const getMessages = async (
    {limit, offset, include_hidden}: { limit: number, offset: number, include_hidden?: boolean }
): Promise<IMessage[]> => {
    const params: Record<string, string | number | boolean> = {
        limit,
        offset,
    };
    if (include_hidden) params.include_hidden = true;
    const {data}: AxiosResponse<IMessage[]> = await baseApi.get<IMessage[]>('/communication', {
        params,
    });
    return data;
};

export const setConversationHidden = async (
    communicationId: number,
    hidden: boolean
): Promise<IMessage> => {
    const {data}: { data: IMessage } = await baseApi.patch<IMessage>(
        `/communication/${communicationId}/hide`,
        {hidden},
    );
    return data;
};

export const getMessageCount = async (): Promise<IMessage[]> => {
    const {data}: AxiosResponse<IMessage[]> = await baseApi.get<IMessage[]>('/communication', {
        params: {
            mode: 'count'
        }
    });
    return data;
};

export const getCandidateFromMsgByIdApi = async (communicationId: string): Promise<ICandidate> => {
    try {
        const {data}: { data: ICandidate } = await baseApi.get<ICandidate>(`/communication/${communicationId}`);
        return data;
    } catch (error) {
        console.error('Error fetching candidate:', error);
        throw error;
    }
};

export const getCandidateMessagesByComId = async (communication_id: number): Promise<IMessageBuble[]> => {
    try {
        const params = {communication_id};
        const {data}: { data: IMessageBuble[] } = await baseApi.get<IMessageBuble[]>(`/message`, {params});
        return data;
    } catch (error) {
        console.error('Error fetching candidate:', error);
        throw error;
    }
};

export const sendMessageApi = async (messageData: {
    message: string;
    type: string;
    client_id: number;
    candidate_id: number;
    job_demand_id: number;
    communication_id: number;
    contact_data: string;
}): Promise<IMessageBuble> => {
    try {
        const {data}: { data: IMessageBuble } = await baseApi.post<IMessageBuble>('/message', messageData);
        return data;
    } catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
};

export const getFinancePlanUrl = async (planKey: string): Promise<string> => {
    try {
        const authToken = getToken();
        const params = {
            id: authToken,
            product: planKey,
        };

        const response = await billingApi.get<{
            url: string
        }>('/checkout_session', {params});
        return response.request.responseURL
    } catch (error) {
        console.error('Error getting finance plan URL:', error);
        throw error;
    }
};

export const getMagicLink = async (email: string): Promise<{
    url: string
}> => {
    try {
        const params = {email};
        const {data}: { data: { url: string } } = await privacyApi.get<{
            url: string
        }>('/auth/request-magic-link', {params});
        return data;
    } catch (error) {
        console.error('Error changing plan:', error);
        throw error;
    }
};

export const getSearches = async (): Promise<ISearchResult> => {
    const {data}: { data: ISearchResult } = await baseApi.get('/job_search');
    return data;
};

export const getSearchById = async (id: number): Promise<ISearchResult> => {
    const {data}: { data: ISearchResult } = await baseApi.get(`/job_search/${id}`);
    return data;
};

export const addSearch = async (searchParam: Partial<IJobDemand>): Promise<IJobDetails> => {
    const {data}: { data: IJobDetails } = await baseApi.post<IJobDetails>(`/job_search`, searchParam);
    return data;
};

export const updateSearchById = async (id: number, updatedData: Partial<IJobDetails>): Promise<IJobDetails> => {
    const {data}: { data: IJobDetails } = await baseApi.patch<IJobDetails>(`/job_search/${id}`, updatedData);
    return data;
};

export const getCompanySize = async (): Promise<CompanySizeType> => {
    const {data}: { data: CompanySizeType } = await baseApi.get<CompanySizeType>("/company_size");
    return data;
};

export const getSectors = async () => {
    const {data} = await baseApi.get<SectorType[]>('/sector');
    return data;
};

export const getSubsectors = async (sector_id: string) => {
    const {data} = await baseApi.get<SectorType[]>('/subsector', {params: {sector_id}});
    return data;
};

export const getExperienceYears = async () => {
    const {data} = await baseApi.get<JobExpType[]>('/experience_years');
    return data;
};
export const getJobExperience = async () => {
    const {data} = await baseApi.get<JobExpType[]>('/employer_years');
    return data;
};
export const getEducationLevels = async () => {
    const {data} = await baseApi.get<JobExpType[]>('/education_level');
    return data;
};
export const getWorkLoadOptions = async () => {
    const {data} = await baseApi.get<JobExpType[]>('/work_load_candidate');
    return data;
};
export const updateCandidate = async ({id, body}: { id: number, body: DeepPartialCandidateProfile }) => {
    const {data} = await baseApi.put(`/candidate/${id}`, body);
    return data;
};

export const updateCandidateEducation = async ({id, body}: { id: number, body: { education: Record<string, unknown> } }) => {
    const {data} = await baseApi.put(`/candidate/${id}/education`, body);
    return data;
};

export const uploadCVDocument = async (file: File): Promise<{ path: string }> => {
    const formData = new FormData();
    formData.append('content', file);

    const {data} = await baseApi.post<{ path: string }>('/upload/attachment', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    return data;
};

export const deleteCVDocument = async (): Promise<void> => {
    await baseApi.delete('/delete/attachment');
};

export const getCandidateMatchingPositions = async () => {
    const {data} = await baseApi.get<MatchingPositionsType[]>('/candidate_matching_positions');
    return data;
};

export const getCandidateMatchingPotJobs = async () => {
    const {data} = await baseApi.get<DesJobType[]>('/candidate_matching_potential_jobs');
    return data;
};

export const getRadiuses = async () => {
    const {data} = await baseApi.get<Radius[]>('/radius');
    return data;
};

export const modifyCandidateMatchingPosition = async ({candidateId, position_id, matching_used}: {
        candidateId: number,
        position_id: number,
        matching_used: boolean
    }) => {
        const {data} = await baseApi.put<JobExpType[]>(`/candidate/${candidateId}`, {
                    candidate_matching_positions: [{
                        id: position_id,
                        matching_used
                    }]
                }
            )
        ;
        return data;
    }
;
export const modifyCandExcludePosSurvey = async (body: {
    users_id: number,
    candidateId: number,
    positions_id: number,
    response: number,
    candidate_matching_position_id: number
}) => {
    const {data} = await baseApi.post<JobExpType[]>(`/exclude_position_survey`, body);
    return data;
};
export const modifyCandDeactivateSurvey = async (body: {
    "users_id": number,
    "candidate_id": number,
    "response": number
}) => {
    const {data} = await baseApi.post<JobExpType[]>(`/deactivate_survey`, body);
    return data;
};


export const addNpsRecord = async (body: {
    "users_id": number,
    "candidate_id": number,
    "points": number
}) => {
    const {data} = await baseApi.post<JobExpType[]>(`/nps_candidate`, body);
    return data;
};

export const getAlertConfigs = async (): Promise<IAlertConfig> => {
    const {data}: { data: IAlertConfig } = await baseApi.get('/alert_config');
    return data;
};

export const deleteUser = async (users_id: number): Promise<IAlertConfig> => {
    const {data}: { data: IAlertConfig } = await baseApi.delete(`/users/${users_id}`);
    return data;
};

export const addCourseToCandidate = async (courseDetail: { course_id: string; course_name: string }) => {
    const {data} = await baseApi.post('/usercourses', courseDetail);
    return data;
};

export const getUserCourses = async (): Promise<IAlertConfig> => {
    const {data}: { data: IAlertConfig } = await baseApi.get('/usercourses/1');
    return data;
};
export const getAvailableCourses = async (): Promise<ICourse[]> => {
    const {data}: { data: ICourse[] } = await coursesApi.get('/candidate_courses');
    return data;
};

export const gerCurrentCourse = async (courseID: string): Promise<ICourse> => {
    const {data}: { data: ICourse } = await coursesApi.get(`/courses/${courseID}`);
    return data;
};

export const getStripeUrl = async ({id, product, redirectPath}: { id: string, product: string, redirectPath: string }) => {
    const {data} = await billingApi.post('/candidate_checkout_session', {
        id,
        product,
        redirectPath
    });
    return data;
};

export const cancelSubscription = async () => {
    const {data} = await billingApi.post('/candidate_cancel_subscription');
    return data;
};

export const getSubscriptionDetails = async () => {
    const {data} = await billingApi.get('/candidate_subscription_details');
    return data;
};

// Job Coach API functions
export const startJobCoachSession = async (request: StartJobCoachRequest): Promise<StartJobCoachResponse> => {
    try {
        const {data}: AxiosResponse<StartJobCoachResponse> = await jobCoachApi.post('/api/job-coach/start', request);
        return data;
    } catch (error) {
        console.error('Error starting job coach session:', error);
        throw error;
    }
};

export const sendJobCoachMessage = async (
    sessionId: string,
    request: SendJobCoachMessageRequest
): Promise<SendJobCoachMessageResponse> => {
    try {
        const {data}: AxiosResponse<SendJobCoachMessageResponse> = await jobCoachApi.post(
            `/api/job-coach/session/${sessionId}/message`,
            request
        );
        return data;
    } catch (error) {
        console.error('Error sending job coach message:', error);
        throw error;
    }
};

export const getJobCoachRecommendations = async (sessionId: string): Promise<string[]> => {
    try {
        const {data}: AxiosResponse<string[]> = await jobCoachApi.get(
            `/api/job-coach/session/${sessionId}/recommendations`
        );
        return data;
    } catch (error) {
        console.error('Error getting job coach recommendations:', error);
        throw error;
    }
};

// Xano AI API functions
export const startXanoAiSession = async () => {
    try {
        const {data} = await xanoAiApi.post('/sessions/start', {
            conversation_type: 'job_coach'
        });
        return data;
    } catch (error) {
        console.error('Error starting Xano AI session:', error);
        throw error;
    }
};

export const sendXanoAiMessage = async (sessionId: string, message: string) => {
    try {
        const {data} = await xanoAiApi.post('/sessions/message', {
            conversation_type: 'job_coach',
            message,
            session_id: sessionId
        });
        return data;
    } catch (error) {
        console.error('Error sending Xano AI message:', error);
        throw error;
    }
};

export const getXanoAiSessions = async () => {
    try {
        const {data} = await xanoAiApi.get('/ai_chat_sessions', {
            params: { conversation_type: 'job_coach' }
        });
        return data;
    } catch (error) {
        console.error('Error getting Xano AI sessions:', error);
        throw error;
    }
};

export const getXanoAiSessionMessages = async (sessionId: string) => {
    try {
        const {data} = await xanoAiApi.get(`/sessions/messages/${sessionId}`);
        return data;
    } catch (error) {
        console.error('Error getting Xano AI session messages:', error);
        throw error;
    }
};

export const startXanoAiInterviewSession = async (jobRole: string | null = null) => {
    try {
        const requestBody: { conversation_type: string, jobRole?: string } = {
            conversation_type: 'job_interview'
        };

        if (jobRole) {
            requestBody.jobRole = jobRole;
        }

        const {data} = await xanoAiApi.post('/sessions/start', requestBody);
        return data;
    } catch (error) {
        console.error('Error starting Xano AI interview session:', error);
        throw error;
    }
};

export const sendXanoAiInterviewMessage = async (sessionId: string, message: string) => {
    try {
        const {data} = await xanoAiApi.post('/sessions/message', {
            conversation_type: 'job_interview',
            message,
            session_id: sessionId
        });
        return data;
    } catch (error) {
        console.error('Error sending Xano AI interview message:', error);
        throw error;
    }
};

export const getXanoAiInterviewSessions = async () => {
    try {
        const {data} = await xanoAiApi.get('/ai_chat_sessions', {
            params: { conversation_type: 'job_interview' }
        });
        return data;
    } catch (error) {
        console.error('Error getting Xano AI interview sessions:', error);
        throw error;
    }
};

export const getXanoAiInterviewSessionMessages = async (sessionId: string) => {
    try {
        const {data} = await xanoAiApi.get(`/sessions/messages/${sessionId}`);
        return data;
    } catch (error) {
        console.error('Error getting Xano AI interview session messages:', error);
        throw error;
    }
};

export const startXanoAiCoverLetterSession = async (jobRole: string | null = null, job_demand_id?: number | null) => {
    try {
        const requestBody: { conversation_type: string, jobRole?: string, job_demand_id?: number } = {
            conversation_type: 'job_letter'
        };

        if (jobRole) {
            requestBody.jobRole = jobRole;
        }

        if (job_demand_id) {
            requestBody.job_demand_id = job_demand_id;
        }

        const {data} = await xanoAiApi.post('/sessions/start', requestBody);
        return data;
    } catch (error) {
        console.error('Error starting Xano AI cover letter session:', error);
        throw error;
    }
};

export const sendXanoAiCoverLetterMessage = async (sessionId: string, message: string) => {
    try {
        const {data} = await xanoAiApi.post('/sessions/message', {
            conversation_type: 'job_letter',
            message,
            session_id: sessionId
        });
        return data;
    } catch (error) {
        console.error('Error sending Xano AI cover letter message:', error);
        throw error;
    }
};

export const getXanoAiCoverLetterSessions = async () => {
    try {
        const {data} = await xanoAiApi.get('/ai_chat_sessions', {
            params: { conversation_type: 'job_letter' }
        });
        return data;
    } catch (error) {
        console.error('Error getting Xano AI cover letter sessions:', error);
        throw error;
    }
};

export const getXanoAiCoverLetterSessionMessages = async (sessionId: string) => {
    try {
        const {data} = await xanoAiApi.get(`/sessions/messages/${sessionId}`);
        return data;
    } catch (error) {
        console.error('Error getting Xano AI cover letter session messages:', error);
        throw error;
    }
};

export const deleteXanoAiSession = async (sessionId: string) => {
    try {
        const {data} = await xanoAiApi.delete(`/ai_chat_sessions/${sessionId}`);
        return data;
    } catch (error) {
        console.error('Error deleting Xano AI session:', error);
        throw error;
    }
};

// AI Jobs Session Management Functions
export const startXanoAiJobSearchSession = async () => {
    try {
        const {data} = await xanoAiApi.post('/sessions/start', {
            conversation_type: 'job_search'
        });
        return data;
    } catch (error) {
        console.error('Error starting Xano AI job search session:', error);
        throw error;
    }
};

export const sendXanoAiJobSearchMessage = async (sessionId: string, message: string) => {
    try {
        const {data} = await xanoAiApi.post('/sessions/message', {
            conversation_type: 'job_search',
            message,
            session_id: sessionId
        });
        return data;
    } catch (error) {
        console.error('Error sending Xano AI job search message:', error);
        throw error;
    }
};

export const getXanoAiJobSearchSessions = async () => {
    try {
        const {data} = await xanoAiApi.get('/ai_chat_sessions', {
            params: {
                conversation_type: 'job_search'
            }
        });
        return data;
    } catch (error) {
        console.error('Error getting Xano AI job search sessions:', error);
        throw error;
    }
};

export const getXanoAiJobSearchSessionMessages = async (sessionId: string) => {
    try {
        const {data} = await xanoAiApi.get(`/sessions/messages/${sessionId}`);
        return data;
    } catch (error) {
        console.error('Error getting Xano AI job search session messages:', error);
        throw error;
    }
};

// Rentensteuerberater (Pension Tax Advisor) Session Management Functions
export const startXanoAiRentensteuerberaterSession = async () => {
    try {
        const {data} = await xanoAiApi.post('/sessions/start', {
            conversation_type: 'job_tax'
        });
        return data;
    } catch (error) {
        console.error('Error starting Xano AI Steuer & Rente Experte session:', error);
        throw error;
    }
};

export const sendXanoAiRentensteuerberaterMessage = async (sessionId: string, message: string) => {
    try {
        const {data} = await xanoAiApi.post('/sessions/message', {
            conversation_type: 'job_tax',
            message,
            session_id: sessionId
        });
        return data;
    } catch (error) {
        console.error('Error sending Xano AI Steuer & Rente Experte message:', error);
        throw error;
    }
};

export const getXanoAiRentensteuerberaterSessions = async () => {
    try {
        const {data} = await xanoAiApi.get('/ai_chat_sessions', {
            params: { conversation_type: 'job_tax' }
        });
        return data;
    } catch (error) {
        console.error('Error getting Xano AI Steuer & Rente Experte sessions:', error);
        throw error;
    }
};

export const getXanoAiRentensteuerberaterSessionMessages = async (sessionId: string) => {
    try {
        const {data} = await xanoAiApi.get(`/sessions/messages/${sessionId}`);
        return data;
    } catch (error) {
        console.error('Error getting Xano AI Steuer & Rente Experte session messages:', error);
        throw error;
    }
};

export const deleteXanoAiRentensteuerberaterSession = async (sessionId: string) => {
    try {
        const {data} = await xanoAiApi.delete(`/ai_chat_sessions/${sessionId}`);
        return data;
    } catch (error) {
        console.error('Error deleting Xano AI Steuer & Rente Experte session:', error);
        throw error;
    }
};

export const getAiChatJobs = async (sessionId?: string) => {
    try {
        const params = (sessionId && typeof sessionId === 'string') ? { sessionId } : {};
        const {data} = await xanoAiApi.get(`/ai_chat_jobs`, { params });
        return data;
    } catch (error) {
        console.error('Error getting AI job search session messages:', error);
        throw error;
    }
};

export const getSilverAgentJobs = async () => {
    try {
        const {data} = await xanoAiApi.get('/jobs');
        return data;
    } catch (error) {
        console.error('Error getting Silver Agent jobs:', error);
        throw error;
    }
};

export const submitFeedback = async (feedbackText: string) => {
    try {
        const {data} = await xanoAiApi.post('/feedback', { message: feedbackText });
        return data;
    } catch (error) {
        console.error('Error submitting feedback:', error);
        throw error;
    }
};

export interface GeneratePdfParams {
    html: string;
    filename?: string;
    includePageBreaks?: boolean;
    pageBreaksFromPreview?: Array<{
        blocks: Array<{ blockId: string; unitIndices: number[] }>;
    }>;
}

export interface GeneratePdfResponse {
    pdfBase64: string;
    pageBreaks?: Array<{
        blocks: Array<{ blockId: string; unitIndices: number[] }>;
    }>;
}

/** CV generator cloud sync (candidate API group: GET/POST /cv_data) */
export type CvGeneratorRemoteGetResponse = {
    has_record: boolean;
    cv_data: Record<string, unknown> | null;
    visible_sections: Record<string, boolean> | null;
    section_order: string[] | null;
    additional_sections: Record<string, unknown> | null;
    cv_settings: Record<string, unknown> | null;
};

export type CvGeneratorRemoteSavePayload = {
    cv_data: Record<string, unknown>;
    visible_sections: Record<string, boolean>;
    section_order: string[];
    additional_sections: Record<string, unknown>;
    cv_settings: Record<string, unknown>;
};

export const getCvGeneratorRemoteState = async (): Promise<CvGeneratorRemoteGetResponse> => {
    const { data } = await baseApi.get<CvGeneratorRemoteGetResponse>('/cv_data');
    return data;
};

export const saveCvGeneratorRemoteState = async (
    body: CvGeneratorRemoteSavePayload
): Promise<CvGeneratorRemoteGetResponse> => {
    const { data } = await baseApi.post<CvGeneratorRemoteGetResponse>('/cv_data', body);
    return data;
};

export const generatePdf = async (
    params: GeneratePdfParams,
    options?: { signal?: AbortSignal; timeout?: number }
): Promise<GeneratePdfResponse> => {
    const { data } = await baseApi.post<GeneratePdfResponse>(
        '/pdf_generate',
        params,
        {
            signal: options?.signal,
            timeout: options?.timeout ?? 120000,
        }
    );
    return data;
};
